import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Stack, Group, Title, Paper, Loader, Center } from '@mantine/core';
import { IconCash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Invoice, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { resolveId } from '@medplum/core';
import { useLocation } from 'react-router-dom';
import { ResourceSearchForm } from '@/components/resource-search/ResourceSearchForm';
import { ResourceTable } from '@/components/resource-search/ResourceTable';
import {
  usePaymentSearch,
} from '../hooks/usePaymentSearch';
import {
  getPaymentSearchFields,
  getPaymentTableColumns,
  PAYMENT_SEARCH_DEFAULTS,
  PaymentSearchParams,
} from './paymentSearchConfig';
import { InvoiceDetailModal } from './InvoiceDetailModal';

// -----------------------------------------------------------------------------
export const PaymentsPage: FC = () => {
  const medplum = useMedplum();
  const location = useLocation();
  const { t } = useTranslation('lims');

  // Use the payment search hook
  const {
    resources: invoices,
    loading,
    page,
    totalPages,
    totalCount,
    setPage,
    patients,
    searchParams,
    updateSearchParams,
    clearSearch,
    fetchResources: fetchInvoices,
    patientNameQuery,
    setPatientNameQuery,
    setPatientQuery,
    patientNameOptions,
    patientLoading,
    patientNameLoading,
  } = usePaymentSearch(medplum);

  // Modal state
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleSearch = useCallback(
    (updates: Partial<PaymentSearchParams>) => {
      const merged = { ...searchParams, ...updates };
      setPage(1);
      updateSearchParams(updates);
      fetchInvoices(merged);
    },
    [fetchInvoices, searchParams, setPage, updateSearchParams]
  );

  const handleClearSearch = useCallback(() => {
    clearSearch(PAYMENT_SEARCH_DEFAULTS);
  }, [clearSearch]);

  // ---------------------------------------------------------------------------
  const fields = useMemo(
    () =>
      getPaymentSearchFields({
        t,
        searchParams,
        onSearch: handleSearch,
        patientNameQuery,
        setPatientNameQuery,
        setPatientQuery,
        patientNameOptions,
        patientLoading,
        patientNameLoading,
      }),
    [
      handleSearch,
      patientLoading,
      patientNameLoading,
      patientNameOptions,
      patientNameQuery,
      searchParams,
      setPatientNameQuery,
      setPatientQuery,
      t,
    ]
  );

  const columns = useMemo(
    () =>
      getPaymentTableColumns({
        t,
        patients,
      }),
    [patients, t]
  );

  const handleInvoiceClick = useCallback(
    (invoice: Invoice) => {
      setSelectedInvoice(invoice);
      // Resolve the patient for the modal
      const patientRef = invoice.recipient?.reference || invoice.subject?.reference;
      if (patientRef?.startsWith('Patient/')) {
        const patientId = resolveId({ reference: patientRef }) ?? '';
        const pat = patients.get(patientId) ?? null;
        setSelectedPatient(pat);
      } else {
        setSelectedPatient(null);
      }
      setModalOpen(true);
    },
    [patients]
  );

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedInvoice(null);
    setSelectedPatient(null);
    fetchInvoices();
  }, [fetchInvoices]);

  return (
    <Stack gap="md" className="p-6">
      <Group justify="space-between">
        <Group gap="sm">
          <IconCash size={24} />
          <Title order={2}>
            {t('paymentsPage.title', 'Payments', { ns: 'lims' })}
          </Title>
        </Group>
      </Group>

      <ResourceSearchForm<PaymentSearchParams>
        fields={fields}
        searchParams={searchParams}
        onSearch={handleSearch}
        onClear={handleClearSearch}
        clearLabel={t('paymentsPage.clear', 'Clear', { ns: 'lims' })}
        layout={{ cols: { base: 1, sm: 2, lg: 3 } }}
      />

      <Paper shadow="sm" withBorder>
        {loading ? (
          <Center p="xl">
            <Loader />
          </Center>
        ) : (
          <ResourceTable<Invoice>
            data={invoices}
            columns={columns}
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={setPage}
            onRowClick={handleInvoiceClick}
            onActionClick={handleInvoiceClick}
            emptyText={t('paymentsPage.noPaymentsFound', 'No payments found', {
              ns: 'lims',
            })}
          />
        )}
      </Paper>

      {/* Invoice Detail Modal */}
      <InvoiceDetailModal
        opened={modalOpen}
        onClose={handleModalClose}
        invoice={selectedInvoice}
        patient={selectedPatient}
      />
    </Stack>
  );
};
